/**
 * Google OAuth Service
 *
 * Handles Google OAuth2 authentication flow using authorization code approach.
 * Provides a complete abstraction for Google authentication including URL generation,
 * token exchange, and session management.
 *
 */

import { GoogleOAuthConfig } from '../../store/models/authModel';
import { GOOGLE_OAUTH_CONSTANTS, POSSIBLE_NONCE } from '../constants/commonConstants';

/**
 * Service class for handling Google OAuth2 authentication
 * Implements authorization code flow with PKCE for security
 */
class GoogleAuthService {
  private readonly config: GoogleOAuthConfig;
  private readonly GOOGLE_AUTH_ENDPOINT = GOOGLE_OAUTH_CONSTANTS.ENDPOINTS.AUTHORIZATION;
  constructor() {
    // Validate required environment variables
    const clientId = process.env.REACT_APP_GOOGLE_OUATH_CLIENT_ID;
    if (!clientId) {
      throw new Error(GOOGLE_OAUTH_CONSTANTS.ERRORS.MISSING_CLIENT_ID);
    }

    this.config = {
      client_id : clientId,
      redirect_uri: window.location.origin + '/google-auth',
      scope: GOOGLE_OAUTH_CONSTANTS.SCOPES.DEFAULT.join(' '),
      response_type: 'code',
      access_type: 'offline',
      include_granted_scopes: 'true',
    };
  }

  // Generate cryptographically secure random string for nonce/state parameter
  private generateNonce(): string {
    const possible = POSSIBLE_NONCE;
    const values = crypto.getRandomValues(new Uint8Array(32));
    return values.reduce((acc: string, x: number): string => acc + possible[x % possible.length], '');
  }

  // Build OAuth2 authorization URL with security parameters
  public buildAuthorizationUrl(originalPath?: string, silentAuth: boolean = false): string {
    const nonce = this.generateNonce();    // Store original path for post-login redirect
    if (originalPath) {
      sessionStorage.setItem(GOOGLE_OAUTH_CONSTANTS.STORAGE_KEYS.DEEPLINK, originalPath);
    }

    // Store nonce for CSRF protection
    sessionStorage.setItem(GOOGLE_OAUTH_CONSTANTS.STORAGE_KEYS.NONCE, nonce);
    const params = new URLSearchParams({
      ...this.config,
      state: nonce,
      prompt: silentAuth ? 'none' : 'consent',
      ...(silentAuth && { authuser: '0' }),
    });

    return `${this.GOOGLE_AUTH_ENDPOINT}?${params.toString()}`;
  }

  private clearAuthenticationState(): void {
    // Clear URL parameters
    window.history.replaceState({}, document.title, window.location.pathname);
    // Clean up session storage
    sessionStorage.removeItem(GOOGLE_OAUTH_CONSTANTS.STORAGE_KEYS.NONCE);
    sessionStorage.removeItem(GOOGLE_OAUTH_CONSTANTS.STORAGE_KEYS.INTERACTIVE_LOGIN);
  }

  // Force (account selection) when silent auth fails
  public forceInteractiveLogin(originalPath?: string): void {
    // Clear any existing OAuth response parameters
    this.clearAuthenticationState();
    // Mark this as an interactive login
    sessionStorage.setItem(GOOGLE_OAUTH_CONSTANTS.STORAGE_KEYS.INTERACTIVE_LOGIN, 'true');
    this.initiateLogin(originalPath, false);
  }

  // Initiate OAuth2 authorization flow
  public initiateLogin(originalPath?: string, silentAuth: boolean = false): void {
    const authUrl = this.buildAuthorizationUrl(originalPath, silentAuth);
    // Mark as interactive login if not silent auth
    if (!silentAuth) {
      sessionStorage.setItem(GOOGLE_OAUTH_CONSTANTS.STORAGE_KEYS.INTERACTIVE_LOGIN, 'true');
    }
    window.location.href = authUrl;
  }

  // Handle deep link redirect after authentication
  public handlePostAuthRedirect(): string {
    const destination = sessionStorage.getItem(GOOGLE_OAUTH_CONSTANTS.STORAGE_KEYS.DEEPLINK);
    if (destination) {
      sessionStorage.removeItem(GOOGLE_OAUTH_CONSTANTS.STORAGE_KEYS.DEEPLINK);
      return destination;
    }
    return '/';
  }

  // Check if current URL contains OAuth response parameters
  public hasOAuthResponse(): boolean {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.has('code') || urlParams.has('error');
  }

  // Attempt silent authentication first, fallback to interactive if needed
  public attemptSilentLogin(originalPath?: string): void {
    if (this.hasOAuthResponse()) {
      return; // OAuth response already present
    }
    this.initiateLogin(originalPath, true);
  }

  public async handleOAuthResponse(): Promise<{
    success: boolean;
    authCode?: string;
    isInteractiveLogin?: boolean;
    error?: string;
  }> {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const { code: authCode, error, state, error_subtype: errorSubtype } = Object.fromEntries(urlParams);

      // Handle specific OAuth errors
      if (error === 'interaction_required' || errorSubtype === 'access_denied') {
        this.forceInteractiveLogin();
        return { success: false, error: GOOGLE_OAUTH_CONSTANTS.ERRORS.AUTHENTICATION_RESPONSE };
      }

      // Validate state parameter for CSRF protection
      const storedNonce = sessionStorage.getItem(GOOGLE_OAUTH_CONSTANTS.STORAGE_KEYS.NONCE);
      if (state && storedNonce && state !== storedNonce) {
        throw new Error(GOOGLE_OAUTH_CONSTANTS.ERRORS.INVALID_STATE);
      }

      if (error) {
        this.clearAuthenticationState();
        return { success: false, error: GOOGLE_OAUTH_CONSTANTS.ERRORS.AUTHENTICATION_RESPONSE };
      }

      if (!authCode) {
        return { success: false, error: GOOGLE_OAUTH_CONSTANTS.ERRORS.AUTHENTICATION_RESPONSE };
      }

      // Check if this was an interactive login before clearing state
      const isInteractiveLogin = sessionStorage.getItem(GOOGLE_OAUTH_CONSTANTS.STORAGE_KEYS.INTERACTIVE_LOGIN) === 'true';
      this.clearAuthenticationState();

      return {
        success: true,
        authCode,
        isInteractiveLogin,
      };
    } catch (error) {
      return {
        success: false,
        error: GOOGLE_OAUTH_CONSTANTS.ERRORS.AUTHENTICATION,
      };
    }
  }
}

// Singleton instance of GoogleAuthService
export const googleAuthService = new GoogleAuthService();
